import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';

// Base screening selectors
export const selectScreeningStatus = (state: RootState) => state.screening;
export const selectScreeningResults = (state: RootState) => state.screening.results;
export const selectScreeningProgress = (state: RootState) => ({
  processed_count: state.screening.processed_count,
  total_stocks: state.screening.total_stocks,
  passing_count: state.screening.passing_count
});

// Computed screening selectors
export const selectScreeningProgressPercentage = createSelector(
  [selectScreeningProgress],
  (progress) => {
    if (progress.total_stocks === 0) return 0;
    return Math.round((progress.processed_count / progress.total_stocks) * 100);
  }
);

export const selectScreeningPassRate = createSelector(
  [selectScreeningProgress],
  (progress) => {
    if (progress.processed_count === 0) return 0;
    return Math.round((progress.passing_count / progress.processed_count) * 100);
  }
);

export const selectIsScreeningActive = createSelector(
  [selectScreeningStatus],
  (screening) => screening.status === 'running' || screening.status === 'loading'
);

export const selectScreeningMetrics = createSelector(
  [selectScreeningStatus],
  (screening) => ({
    status: screening.status,
    progress: screening.total_stocks > 0
      ? Math.round((screening.processed_count / screening.total_stocks) * 100)
      : 0,
    passRate: screening.processed_count > 0
      ? Math.round((screening.passing_count / screening.processed_count) * 100)
      : 0,
    eta: screening.total_stocks > 0 && screening.processed_count > 0
      ? Math.round(((screening.total_stocks - screening.processed_count) / screening.processed_count) * 2) // rough estimate in minutes
      : null,
    currentStock: screening.current_stock,
    message: screening.message
  })
);

// Screening performance tracking
export const selectScreeningPerformance = createSelector(
  [selectScreeningStatus],
  (screening) => {
    const totalProcessed = screening.processed_count;
    const totalPassing = screening.passing_count;
    const totalFailed = screening.failed_count || 0;
    const totalErrors = screening.error_count || 0;

    return {
      totalProcessed,
      totalPassing,
      totalFailed,
      totalErrors,
      successRate: totalProcessed > 0 ? Math.round((totalPassing / totalProcessed) * 100) : 0,
      errorRate: totalProcessed > 0 ? Math.round((totalErrors / totalProcessed) * 100) : 0,
      efficiency: totalProcessed > 0 ? Math.round(((totalPassing + totalFailed) / totalProcessed) * 100) : 0
    };
  }
);